import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  HStack,
  Image,
  Text,
} from '@chakra-ui/react';
import { useQueries } from '@tanstack/react-query';
import _ from 'lodash';
import React from 'react';
import { Else, If, Then } from 'react-if';

import { FileValidationBadges } from './FileValidationBadges';
import { OpenFileButton } from './OpenFileButton';
import { truefileServerApi } from 'api/truefilesServerApi';
import { IFileAnalysisResponse } from 'api/types/trueFileServerTypes';
import RunningAnalysisIcon from 'assets/icons/running-analysis-icon.svg';
import useApplicationContext from 'contexts/application/useApplicationContext';

export default function ApplicantSection(): React.ReactElement {
  const { application } = useApplicationContext();
  const files = _.orderBy(application.files || [], ['dateCreated'], ['desc']);

  const results = useQueries({
    queries: files.map((file) => ({
      queryKey: ['analysis', file.id],
      enabled: !!file?.id,
      queryFn: async (): Promise<IFileAnalysisResponse> => {
        const { data } = await truefileServerApi.GET(
          '/api/files/analysis/{fileId}',
          {
            params: {
              path: { fileId: file.id! },
            },
          },
        );
        if (!data) throw new Error('No analysis data returned');
        return data;
      },
      refetchInterval: ({ state: { data } }: { state: { data?: IFileAnalysisResponse } }) => {
        const status = data?.status?.toLowerCase();
        return status === 'success' || status === 'failed' ? false : 3000;
      },
      retry: 10,
    })),
  });

  return (
    <Accordion allowToggle>
      {files.map((file, i) => {
        const res = results[i];
        const errors = res?.data?.validationResult?.errors?.filter((e) => e.severity === 'Error') || [];
        const warnings = res?.data?.validationResult?.errors?.filter((e) => e.severity === 'Warning') || [];

        const isFailed = res?.data?.status === 'failed' || res?.isError;
        const isRunning = !isFailed && !(res?.data?.status === 'success');
        const isSuccessful = res?.data?.status === 'success' && errors.length === 0 && warnings.length === 0;

        return (
          <AccordionItem
            key={file.id}
            border='0'
            mb={4}
            borderRadius='lg'
            bg='gray.50'
          >
            <AccordionButton
              h='100px' borderRadius='lg'
              bg='gray.50'
            >
              <AccordionIcon mr={2} />
              <Flex
                flex='1' justify='space-between'
                align='center'
              >
                <HStack spacing={8}>
                  <Text fontWeight='semibold' textTransform='capitalize'>
                    <If
                      condition={res?.data?.status === 'failed' || res?.isError}
                    >
                      <Then>Analysis Failed</Then>
                      <Else>
                        {res?.data?.fileType?.name || 'Analysis In Progress'}
                      </Else>
                    </If>
                  </Text>

                  <HStack spacing={2}>
                    {Object.values(res?.data?.tags || {}).map((t) => (
                      <Badge
                        key={t}
                        px={3}
                        py={0.5}
                        border='1px solid'
                        borderColor='gray.300'
                        borderRadius='full'
                      >
                        {t}
                      </Badge>
                    ))}
                  </HStack>
                </HStack>

                <HStack spacing={4}>
                  <FileValidationBadges data={res.data} />
                  <OpenFileButton file={file} />
                </HStack>
              </Flex>
            </AccordionButton>

            {/* File Analysis Details */}
            <AccordionPanel py={2} px={4}>
              <If condition={isRunning}>
                <Then>
                  <Box
                    bg='#C5E3FF'
                    p={3}
                    mb={2}
                    borderRadius='md'
                    display='flex'
                    alignItems='center'
                  >
                    <Image
                      src={RunningAnalysisIcon}
                      style={{ width: 27, height: 27, marginRight: 4 }}
                    />
                    <Text>Running AI Analysis…</Text>
                  </Box>
                </Then>
              </If>

              <If condition={isFailed}>
                <Then>
                  <Box
                    bg='#FEE2E2'
                    p={3}
                    mb={2}
                    borderRadius='md'
                    display='flex'
                    alignItems='center'
                  >
                    <Box>
                      <Text
                        fontWeight='bold' color='red.600'
                        mb={1}
                      >
                        Critical Issues Detected
                      </Text>

                      <If condition={res?.isError && res.error?.message}>
                        <Then>
                          <Text
                            fontSize='sm' mt={1}
                            pl={3}
                          >
                            • {res.error?.message}
                          </Text>
                        </Then>
                        <Else>
                          <Text
                            fontSize='sm' mt={1}
                            pl={3}
                          >
                            • The analysis could not be completed due to an
                            unexpected error. Please try again later or contact
                            support.
                          </Text>
                        </Else>
                      </If>
                    </Box>
                  </Box>
                </Then>
              </If>

              <If condition={isSuccessful}>
                <Then>
                  <Box
                    bg='#E6FFFA'
                    p={3}
                    mb={2}
                    borderRadius='md'
                    display='flex'
                    alignItems='center'
                  >
                    <Flex
                      justifyContent='space-between'
                      alignItems='center'
                      mb={1}
                      width='100%'
                    >
                      <Text fontWeight='bold' color='green.600'>
                        Passed
                      </Text>
                    </Flex>
                  </Box>
                </Then>
              </If>

              <If condition={errors.length > 0}>
                <Then>
                  <Box
                    bg='#FFE5E5' p={3}
                    mb={2} borderRadius='md'
                  >
                    <Flex
                      justifyContent='space-between'
                      alignItems='center'
                      mb={1}
                    >
                      <Text fontWeight='bold' color='red.600'>
                        Critical Issues
                      </Text>
                    </Flex>
                    {errors.map((e) => (
                      <Text
                        key={e.errorMessage} fontSize='sm'
                        pl={3}
                      >
                        • {e.errorMessage}
                      </Text>
                    ))}
                  </Box>
                </Then>
              </If>

              <If condition={warnings.length > 0}>
                <Then>
                  <Box
                    bg='#FFF7CC' p={3}
                    borderRadius='md'
                  >
                    <Flex
                      justifyContent='space-between'
                      alignItems='center'
                      mb={1}
                    >
                      <Text fontWeight='bold' color='yellow.800'>
                        Warnings
                      </Text>
                    </Flex>
                    {warnings.map((w) => (
                      <Text
                        key={w.errorMessage} fontSize='sm'
                        pl={3}
                      >
                        • {w.errorMessage}
                      </Text>
                    ))}
                  </Box>
                </Then>
              </If>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
